import { todayISO } from "@/lib/api";

// ============================================================================
// Constantes y helpers puros extraídos de FacturasPage.jsx
// ============================================================================

export const TIPOS_PRODUCTO_VENTA = [
  "Media Res Vacuna", "Cuarto Delantero", "Cuarto Trasero", "Costilla", "Vacío", "Bife Angosto", "Otros",
];

export const TIPOS_PRODUCTO_COMPRA = [
  "Hacienda en Pie", "Media Res", "Cerdo", "Ovino", "Insumos", "Otros",
];

export const TIPOS_COMPROBANTE_MANUAL = [
  "FACTURA A", "FACTURA B", "FACTURA C", "FACTURA E",
  "NOTA DE CREDITO A", "NOTA DE CREDITO B", "NOTA DE DEBITO A", "NOTA DE DEBITO B",
  "REMITO X", "PEDIDO", "TICKET", "OTRO",
];

// Tipos de comprobante que se emiten electrónicamente en AFIP
export const TIPOS_FISCALES = new Set([
  "FACTURA A", "FACTURA B", "FACTURA C",
  "NOTA DE CREDITO A", "NOTA DE CREDITO B", "NOTA DE CREDITO C",
  "NOTA DE DEBITO A", "NOTA DE DEBITO B", "NOTA DE DEBITO C",
]);

export const esFiscal = (t) => TIPOS_FISCALES.has((t || "").toUpperCase().trim());

export const requiereCuit = (t) => {
  const T = (t || "").toUpperCase().trim();
  return T === "FACTURA A" || T === "NOTA DE CREDITO A" || T === "NOTA DE DEBITO A";
};

// Sólo permitimos asociar Remitos Cárnicos en Factura A (obligación legal del rubro).
export const esFacturaA = (t) => (t || "").toUpperCase().trim() === "FACTURA A";

// Nota de Crédito → AFIP exige CbteAsoc con la factura original.
export const esNotaCredito = (t) => {
  const T = (t || "").toUpperCase().trim();
  return T === "NOTA DE CREDITO A" || T === "NOTA DE CREDITO B" || T === "NOTA DE CREDITO C";
};

// Nota de Débito → mismo requerimiento AFIP que NC.
export const esNotaDebito = (t) => {
  const T = (t || "").toUpperCase().trim();
  return T === "NOTA DE DEBITO A" || T === "NOTA DE DEBITO B" || T === "NOTA DE DEBITO C";
};

// Tipo AFIP por defecto para asociar según el comprobante que se está emitiendo.
// NC A / ND A → Factura A (1). NC B / ND B → Factura B (6). NC C / ND C → Factura C (11).
// Factura A → Remito Cárnico Bovino (995) por defecto.
export const defaultTipoAsociado = (tipoComprobante) => {
  const T = (tipoComprobante || "").toUpperCase().trim();
  if (T.endsWith(" A")) return T.startsWith("FACTURA") ? 995 : 1;
  if (T.endsWith(" B")) return 6;
  if (T.endsWith(" C")) return 11;
  return 995;
};

export const UNIDADES_NEGOCIO = ["Mostrador", "Logistica", "Mayorista"];

export const EMPTY_FILTERS = {
  tipo_comprobante: "__all__",
  contraparte_id: "__all__",
  unidad_negocio: "__all__",
  estado: "__all__",
  fecha_desde: "",
  fecha_hasta: "",
  q: "",
};

export const initForm = (defaults = {}) => ({
  numero: "",
  tipo_comprobante: "REMITO X",
  unidad_negocio: defaults.unidad_negocio || "Mostrador",
  fecha: todayISO(),
  fecha_vencimiento: todayISO(),
  contraparte_id: "",
  moneda: "ARS",
  cotizacion: 1,
  iva_pct: 10.5,
  romaneo: "",
  observaciones: "",
  items: [{ descripcion: "", tipo_producto: "", codigo: "", kilos: 0, cantidad: 1, precio_unitario: 0 }],
  medio_pago: "cta_cte",  // "cta_cte" o caja_id
  cheque_info: null,       // datos del cheque cuando el medio_pago apunta a caja tipo="cheques"
  // Cobro combinado: si es array (no null), ignora `medio_pago` y crea N cobros.
  // Cada línea: { medio_pago: caja_id, monto: number, cheque_info?: {...} }
  pagos_combinados: null,
  actualiza_costo: true,   // (compras) actualizar precio de última compra. La compra NO mueve stock.
  cobrar_efectivo_en_ruta: false, // (ventas cta_cte) el chofer cobra el total en efectivo en la ruta
  lista_precios_id: "",
  vto_touched: false, // si el usuario editó manualmente, no auto-recalcular
  comprobantes_asociados: [], // [{tipo: 995, pto_vta, nro, fecha}]
  // Condición IVA del receptor: si es "" usa la del cliente. Si el operador
  // elige otro valor, sobrescribe sólo para esta factura sin tocar la ficha.
  condicion_iva_receptor_override: "",
  // Código de descuento (cupón) manual: si coincide con el `codigo` de una
  // promoción activa, esa promo se desbloquea. Promos sin código aplican siempre.
  codigo_promo: "",
});

// Tipos de comprobantes asociados soportados (Remitos Electrónicos Cárnicos + Facturas
// originales para Notas de Crédito/Débito).
export const TIPOS_COMP_ASOC = [
  { value: 1,   label: "Factura A (1)" },
  { value: 6,   label: "Factura B (6)" },
  { value: 11,  label: "Factura C (11)" },
  { value: 2,   label: "Nota de Débito A (2)" },
  { value: 7,   label: "Nota de Débito B (7)" },
  { value: 12,  label: "Nota de Débito C (12)" },
  { value: 995, label: "Remito Elect. Cárnico Bovino (995)" },
  { value: 996, label: "Remito Elect. Cárnico Porcino (996)" },
  { value: 91,  label: "Remito Común (91)" },
];

export const estadoBadge = {
  pendiente: "text-amber-700 bg-amber-50",
  parcial: "text-blue-700 bg-blue-50",
  pagada: "text-emerald-700 bg-emerald-50",
  anulada: "text-red-700 bg-red-50 line-through",
  aplicada: "text-purple-700 bg-purple-50",
};

export const unidadColor = {
  Mostrador: "text-blue-700 bg-blue-50",
  Logistica: "text-purple-700 bg-purple-50",
  Mayorista: "text-emerald-700 bg-emerald-50",
};
